// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { useState, useEffect } from "react"
import { Box, FormLabel, MenuItem, TextField } from "@mui/material"
import { productsApi } from "@crm-src/lib/products-api"
import type { Product, ProductFeature } from "@crm-src/lib/products-api"
import { Loader2 } from "lucide-react"

interface ProductSelectorProps {
  productId?: string
  productFeatureId?: string
  onProductChange?: (productId: string | undefined) => void
  onProductFeatureChange?: (featureId: string | undefined) => void
  disabled?: boolean
  required?: boolean
}

const spinSx = {
  display: "inline-flex",
  animation: "spin 1s linear infinite",
  "@keyframes spin": { to: { transform: "rotate(360deg)" } },
} as const

export function ProductSelector({
  productId,
  productFeatureId,
  onProductChange,
  onProductFeatureChange,
  disabled = false,
  required = false
}: ProductSelectorProps) {
  const [loading, setLoading] = useState(true)
  const [products, setProducts] = useState<Product[]>([])
  const [productFeatures, setProductFeatures] = useState<{ [productId: number]: ProductFeature[] }>({})

  // Available options based on current selections
  const [availableFeatures, setAvailableFeatures] = useState<ProductFeature[]>([])

  // Load product hierarchy on component mount
  useEffect(() => {
    const loadProductHierarchy = async () => {
      try {
        setLoading(true)
        const hierarchy = await productsApi.getProductHierarchy()
        setProducts(hierarchy.products || [])
        setProductFeatures(hierarchy.productFeatures || {})
      } catch (error) {
        console.error('❌ ProductSelector - Error loading product hierarchy:', error)
      } finally {
        setLoading(false)
      }
    }

    loadProductHierarchy()
  }, [])

  // Update available features when product selection changes
  useEffect(() => {
    if (productId) {
      const selectedProductId = parseInt(productId)
      const features = productFeatures[selectedProductId] || []
      setAvailableFeatures(features)
    } else {
      setAvailableFeatures([])
    }
  }, [productId, productFeatures])

  // Handle clearing dependent selections separately
  useEffect(() => {
    if (productFeatureId && availableFeatures.length > 0) {
      // Check if current feature is still valid
      if (!availableFeatures.some(f => f.id.toString() === productFeatureId)) {
        onProductFeatureChange?.(undefined)
      }
    }
  }, [availableFeatures, productFeatureId, onProductFeatureChange])

  const handleProductChange = (value: string) => {
    const newProductId = value === "none" ? undefined : value
    onProductChange?.(newProductId)

    // Clear dependent selections when product changes
    if (newProductId !== productId) {
      onProductFeatureChange?.(undefined)
    }
  }

  const handleFeatureChange = (value: string) => {
    const newFeatureId = value === "none" ? undefined : value
    onProductFeatureChange?.(newFeatureId)

    // Clear dependent selection when feature changes
    if (newFeatureId !== productFeatureId) {
    }
  }

  const gridSx = { display: "grid", gap: 1, gridColumn: "span 3", gridTemplateColumns: "repeat(2, minmax(200px, 1fr))" } as const

  if (loading) {
    return (
      <Box sx={gridSx}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>Product</FormLabel>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 40, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Box component="span" sx={spinSx}><Loader2 size={16} /></Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormLabel sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>Feature</FormLabel>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 40, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Box component="span" sx={spinSx}><Loader2 size={16} /></Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={gridSx}>
      {/* Product Selection */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <FormLabel htmlFor="product" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
          Product {required && <Box component="span" sx={{ color: "error.main" }}>*</Box>}
        </FormLabel>
        <TextField
          id="product"
          select
          size="small"
          fullWidth
          value={productId && products.some(p => p.id.toString() === productId) ? productId : "none"}
          onChange={(e) => handleProductChange(e.target.value)}
          disabled={disabled || loading}
          SelectProps={{
            displayEmpty: true,
            renderValue: (val) => {
              const selected = val as string
              const product = products.find(p => p.id.toString() === selected)
              if (selected === "none" || !product) {
                return <Box component="span" sx={{ color: "text.disabled" }}>{loading ? "Loading..." : products.length === 0 ? "No products available" : "Select product..."}</Box>
              }
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{product.name}</span>
                  {product.code && <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>({product.code})</Box>}
                </Box>
              )
            },
          }}
        >
          <MenuItem value="none">None</MenuItem>
          {products.map((product) => (
            <MenuItem key={product.id} value={product.id.toString()}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>{product.name}</span>
                {product.code && (
                  <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>({product.code})</Box>
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Product Feature Selection */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <FormLabel htmlFor="product-feature" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>Product Feature</FormLabel>
        <TextField
          id="product-feature"
          select
          size="small"
          fullWidth
          value={productFeatureId && availableFeatures.some(f => f.id.toString() === productFeatureId) ? productFeatureId : "none"}
          onChange={(e) => handleFeatureChange(e.target.value)}
          disabled={disabled || !productId || availableFeatures.length === 0}
          SelectProps={{
            displayEmpty: true,
            renderValue: (val) => {
              const selected = val as string
              const feature = availableFeatures.find(f => f.id.toString() === selected)
              if (selected === "none" || !feature) {
                return (
                  <Box component="span" sx={{ color: "text.disabled" }}>
                    {!productId
                      ? "Select product first..."
                      : availableFeatures.length === 0
                        ? "No features available"
                        : "Select feature..."}
                  </Box>
                )
              }
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>{feature.name}</span>
                  {feature.code && <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>({feature.code})</Box>}
                </Box>
              )
            },
          }}
        >
          <MenuItem value="none">None</MenuItem>
          {availableFeatures.map((feature) => (
            <MenuItem key={feature.id} value={feature.id.toString()}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>{feature.name}</span>
                {feature.code && (
                  <Box component="span" sx={{ fontSize: 12, color: "text.secondary" }}>({feature.code})</Box>
                )}
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Box>

    </Box>
  )
}
